import { PageHero } from '@/components/PageHero';
import { useSeo } from '@/seo/useSeo';

export function Terms(): JSX.Element {
  useSeo({
    title: 'Terms of use',
    description:
      'Plain-language terms for nexusrmm.ai — an early, pre-GA policy for this marketing site, not a finished commercial agreement. Full terms will be finalized before general availability.',
    path: '/terms',
  });

  return (
    <div>
      <PageHero
        eyebrow="Legal"
        title="Terms of use"
        subtitle="Plain-language and deliberately early-stage, same as the product itself. Effective July 13, 2026."
      />

      <section className="pb-16 md:pb-24">
        <article className="container-content max-w-[720px]">
          <p className="text-[15px] leading-relaxed text-white/75">
            This is a plain-language policy for nexusrmm.ai, the marketing and informational site
            you&apos;re on now — not a finished commercial services agreement. Nexus is pre-GA, and
            we&apos;d rather publish an honest, early version of this page than a longer one that
            implies more legal formality than actually exists yet. Full terms of service — the ones
            that will govern the live product — get finalized as part of general availability, and
            design partners work under a separate agreement covering their specific engagement in
            the meantime.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">Using this site</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            This site is informational — it describes Nexus, its roadmap, and its design-partner
            cohort. You&apos;re welcome to read, link to, and share anything on it. You&apos;re not
            permitted to scrape, copy, or republish its content wholesale, misrepresent it as your
            own, or use it to impersonate Nexus or Elemental TS.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            The Nexus name and brand
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            &quot;Nexus,&quot; the Nexus mark, and this site&apos;s design belong to Elemental TS. Third-party
            product and vendor names referenced on this site (Syncro, Jamf, Microsoft Intune,
            NinjaOne, Action1, FortiGate, Meraki, UniFi, and others) are the trademarks of their
            respective owners, referenced here only to describe how Nexus integrates with them —
            not to imply endorsement by those companies.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            No warranty, pre-GA
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            Content on this site — including roadmap timing, module status labels, and capability
            descriptions — reflects our honest understanding of where the platform stands as of the
            date published, and can change as the product actually changes. It&apos;s provided for
            information, not as a guarantee of future availability, timing, or specific
            functionality. Where something is uncertain, we try to say so directly rather than
            imply more certainty than we have.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            The design-partner cohort
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            Joining the private-preview list or expressing interest through this site is not itself
            a binding commitment on either side. If we invite you into the design-partner cohort,
            the specific terms of that engagement — data handling, support expectations, feedback
            arrangements — are agreed to directly and separately before you run real client data
            through the platform.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">Changes</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            We&apos;ll update the effective date above if this page changes in a way that matters, the
            same commitment made on the privacy policy.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">Contact</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            Questions about these terms:{' '}
            <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
              hello@nexusrmm.ai
            </a>
            .
          </p>
        </article>
      </section>
    </div>
  );
}
